import { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { LogoLoading } from '@/components/shared/loadings/logo';
import { RideMapBackdrop } from './map/rideMapBackdrop';
import { blockDismiss, Drawer, DrawerContent } from '@/components/ui/drawer';
import { BookingCancelSheet } from '@/components/shared/booking/actions/cancel-sheet';
import { cancelRide } from '@/services/platform/data/booking/rides';
import { isRideConfirmationReady, isRideCancelled } from '@/services/platform/data/booking/rides/rideStatus';
import { formatDate, formatDateTimeFromDate, formatTime } from '@/lib/core/utils';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore, useActiveRideStore } from '@/lib/core';
import type { RangeRoverDriverInfoProps } from './types';
import { ScreenHead } from '@/components/shared/screen/head';

const LISBON_CENTER = { lat: 38.7223, lng: -9.1393 };

function RangeRoverHeader() {
  const t = useClientTranslation();
  return (
    <div className="pt-safe-area relative shrink-0 bg-white">
      <ScreenHead title={t('App.Labels.RangeRover')} className="pt-0" />
    </div>
  );
}

function RideActionButtons({
  showCancelRide,
  cancelLabel,
  backToHomeLabel,
  onCancel,
  onGoHome,
  className,
}: {
  showCancelRide: boolean;
  cancelLabel: string;
  backToHomeLabel: string;
  onCancel: () => void;
  onGoHome: () => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2 border-t-[0.5px] border-[#c8c8c8] px-4 pt-6', className)}>
      {showCancelRide && (
        <Button variant="primary-solid" palette="dark" size="md" className="w-full" onClick={onCancel}>
          {cancelLabel}
        </Button>
      )}
      <Button variant="secondary-outline" palette="dark" size="md" className="w-full" onClick={onGoHome}>
        {backToHomeLabel}
      </Button>
    </div>
  );
}

// ── RangeRoverDriverInfo ──────────────────────────────────────────────────────

export function RangeRoverDriverInfo({ cmsProps, booking, onGoHome, onSuccess }: RangeRoverDriverInfoProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(s => s.locale);
  const ride = useActiveRideStore(s => s.ride);
  const clearActiveRide = useActiveRideStore(s => s.clearActiveRide);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [autoCancelOpen, setAutoCancelOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  // Ride reached the destination (paid or not) — hand off to the confirmation step.
  // `onSuccess` carries the final ride snapshot forward into local state before the
  // store is cleared, so the confirmation step still has everything it needs.
  useEffect(() => {
    if (isRideConfirmationReady(ride?.status)) {
      onSuccess?.(ride!);
      clearActiveRide();
    }
  }, [ride, onSuccess, clearActiveRide]);

  // Ride was cancelled remotely (by the driver, dispatch, or Onde itself) — the
  // backend already terminated it, so just surface the notice and redirect home.
  useEffect(() => {
    if (isRideCancelled(ride?.status)) setAutoCancelOpen(true);
  }, [ride?.status]);

  const cancelLabel = cmsProps?.cancelRideButtonLabel ?? t('App.Labels.CancelRide');
  const backToHomeLabel = cmsProps?.backToHomeButtonLabel ?? t('App.Labels.GoBackHome');

  const cancelSheets = (
    <>
      <BookingCancelSheet
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirmCancel={async () => {
          if (!ride) return;
          // Clear the local/persisted active-ride state regardless of whether the
          // cancel request itself succeeds — the user asked to cancel, so the Home
          // banner and tracking shouldn't keep pointing at this ride either way.
          const ok = await cancelRide(ride.publicGuid, locale);
          clearActiveRide();
          if (!ok) throw new Error('Cancel ride failed');
        }}
        onSuccess={onGoHome}
        successText={t('App.Labels.RideCancelled')}
        title={cancelLabel}
      />
      <BookingCancelSheet
        open={autoCancelOpen}
        onOpenChange={setAutoCancelOpen}
        initialStatus="success"
        onConfirmCancel={async () => {}}
        onSuccess={() => {
          clearActiveRide();
          onGoHome();
        }}
        successText={t('App.Labels.RideCancelled')}
      />
    </>
  );

  if (!ride || !ride.driver) {
    return (
      <>
        <LogoLoading
          header={<RangeRoverHeader />}
          caption={{
            title: t('App.Labels.FindingDriverTitle'),
            subtitle: t('App.Labels.FindingDriverSubtitle'),
          }}
          footer={
            <RideActionButtons
              showCancelRide
              cancelLabel={cancelLabel}
              backToHomeLabel={backToHomeLabel}
              onCancel={() => setCancelOpen(true)}
              onGoHome={onGoHome}
              className="bg-white pb-10.5"
            />
          }
          className="bg-white"
        />
        {cancelSheets}
      </>
    );
  }

  const phase =
    ride.status === 'ARRIVED' ? 'arrived' : ride.status === 'TRANSFERRING' ? 'transferring' : 'enroute';

  const minutesToArrival = ride.eta
    ? Math.max(0, Math.round((new Date(ride.eta).getTime() - now) / 60000))
    : null;

  const pickupLocation = booking.pickupPoint?.name ?? ride.pickupName ?? ride.pickupAddress ?? '';
  const dropoffLocation = booking.dropoffPoint?.name ?? ride.dropoffName ?? ride.dropoffAddress ?? '';
  const driverName = ride.driver.name ?? '';

  const whenDisplay =
    booking.date && booking.time
      ? `${formatDate(booking.date.toISOString())}, ${formatTime(booking.time)}`
      : formatDateTimeFromDate(ride.pickupTime, locale);

  const smallLabel =
    phase === 'arrived'
      ? t('App.Labels.DriverHasArrived')
      : phase === 'transferring'
        ? t('App.Labels.EnRouteToDestination')
        : t('App.Labels.DriverArrivesIn');

  const bigLine =
    phase === 'arrived' ? (
      t('App.Labels.ReadyWhenYouAre')
    ) : phase === 'transferring' ? (
      t('App.Labels.HeadingTo', { destination: dropoffLocation })
    ) : minutesToArrival != null ? (
      t('App.Labels.DriverArrivesInMinutes', { count: minutesToArrival })
    ) : (
      <>&mdash;</>
    );

  const secondaryLine =
    phase === 'arrived'
      ? t('App.Labels.PickupPoint', { location: pickupLocation })
      : phase === 'transferring'
        ? t('App.Labels.OnYourWayWith', { driverName })
        : t('App.Labels.MeetOutside', { location: pickupLocation });

  const showPhone = true; //phase !== 'transferring';
  const showCancelRide = phase !== 'transferring';

  return (
    <>
      <Drawer open onOpenChange={blockDismiss}>
        <DrawerContent
          overlayClassName="bg-transparent"
          overlayContent={
            <>
              <RideMapBackdrop
                center={ride.currentLocation ?? ride.pickup ?? LISBON_CENTER}
                pickupMarker={ride.pickup}
                dropoffMarker={ride.dropoff}
                vehicleMarker={ride.currentLocation}
                className="absolute inset-0"
              />
              <RangeRoverHeader />
            </>
          }
          disableSwipe
          className="max-h-[80dvh] gap-8 pt-8 pb-8 drop-shadow-[0px_0px_50px_rgba(0,0,0,0.05)]"
        >
          <div className="flex flex-col gap-4 overflow-y-auto px-4">
            <div>
              <div className="flex items-start gap-8">
                <div className="flex flex-1 flex-col items-start justify-center text-left tracking-[0.5px]">
                  <span className="font-pp-neue-montreal text-[14px] text-[#707070]">{smallLabel}</span>
                  <span className="font-pp-neue-montreal text-[32px] leading-[110%] font-medium text-[#1a1a1a]">
                    {bigLine}
                  </span>
                </div>
              </div>

              <p className="font-pp-neue-montreal pt-1 text-[16px] tracking-[0.25px] text-[#1a1a1a]">
                {secondaryLine}
              </p>
            </div>

            {phase === 'arrived' ? (
              <div className="flex flex-col">
                <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#666]">
                  {t('App.Labels.DriverWaitingOutside', { driverName })}
                </p>
                <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#666]">
                  {t('App.Labels.ComplimentaryWaitingTime')}
                </p>
                <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#666]">
                  {t('App.Labels.WaitingFeeNotice')}
                </p>
              </div>
            ) : (
              <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#666]">
                {phase === 'transferring'
                  ? t('App.Labels.EnjoyYourRide')
                  : t('App.Labels.DriverOnTheWay', { driverName })}
              </p>
            )}

            <div className="flex flex-col gap-2.5 pt-4">
              <span className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#656579]">
                {t('App.Labels.MeetYourDriver')}
              </span>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {ride.driver.imageUrl && (
                    <img
                      src={ride.driver.imageUrl}
                      alt=""
                      className="size-9 shrink-0 rounded-full object-cover"
                    />
                  )}
                  <div className="flex items-center gap-2">
                    <p className="font-pp-neue-montreal text-[12px] font-semibold tracking-[0.5px] text-[#2e2e38]">
                      {ride.driver.name}
                    </p>
                    {ride.car?.plateNumber && (
                      <>
                        <span className="text-[4px] text-[#bababa]">●</span>
                        <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#656579]">
                          {ride.car.plateNumber}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                {showPhone && ride.driver.phone && (
                  <a
                    href={`tel:${ride.driver.phone}`}
                    aria-label={t('App.Labels.Phone')}
                    className="flex size-11 shrink-0 items-center justify-center rounded-full"
                  >
                    <Icon name="phone" width={20} height={20} className="text-[#1a1a1a]" />
                  </a>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-col">
              <button
                type="button"
                onClick={() => setIsSummaryExpanded(prev => !prev)}
                aria-expanded={isSummaryExpanded}
                className="flex w-full cursor-pointer items-center justify-between bg-[#f4f4f4] py-4 pl-4"
              >
                <span className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase">
                  {t('App.Labels.ViewBookingSummary')}
                </span>
                <span className="flex shrink-0 items-center justify-center px-3">
                  <Icon
                    name={isSummaryExpanded ? 'nav-arrow-up' : 'nav-arrow-down'}
                    width={20}
                    height={20}
                    className="text-[#1a1a1a]"
                  />
                </span>
              </button>
              <div
                className={cn(
                  'grid bg-[#f4f4f4] transition-[grid-template-rows] duration-300 ease-in-out',
                  isSummaryExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
                aria-hidden={!isSummaryExpanded}
              >
                <div className="overflow-hidden">
                  <div className="flex flex-col gap-4 px-4 pt-2 pb-4">
                    <div className="flex items-start justify-between gap-6">
                      <span className="font-pp-neue-montreal text-[12px] text-black uppercase">
                        {t('App.Labels.WhereFrom')}
                      </span>
                      <span className="font-pp-neue-montreal text-right text-[14px] text-[#1a1a1a]">
                        {ride.pickupName ?? ride.pickupAddress}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-6 uppercase">
                      <span className="font-pp-neue-montreal text-[12px] text-black">
                        {t('App.Labels.WhereTo')}
                      </span>
                      <span className="font-pp-neue-montreal text-right text-[14px] text-[#1a1a1a]">
                        {ride.dropoffName ?? ride.dropoffAddress}
                      </span>
                    </div>
                    {whenDisplay && (
                      <div className="flex items-start justify-between gap-6 uppercase">
                        <span className="font-pp-neue-montreal text-[12px] text-black">
                          {t('App.Labels.When')}
                        </span>
                        <span className="font-pp-neue-montreal text-right text-[14px] text-[#1a1a1a]">
                          {whenDisplay}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <RideActionButtons
            showCancelRide={showCancelRide}
            cancelLabel={cancelLabel}
            backToHomeLabel={backToHomeLabel}
            onCancel={() => setCancelOpen(true)}
            onGoHome={onGoHome}
          />
        </DrawerContent>
      </Drawer>

      {cancelSheets}
    </>
  );
}
